import type { StockfishSearchProgress } from "@/features/analysis/stockfish-worker";
import { liveEval } from "@/features/analysis/position-analysis-service";

export { LiveEvalService, liveEval, positionEvaluationScore } from "@/features/analysis/position-analysis-service";

/**
 * In-memory store for evaluate_line results, keyed by a short id Nikola
 * is given and told to echo back via [EVAL_LINE: <id>] — lets
 * InlineBoard render a real steppable navigator (◀▶) for a live
 * Stockfish line the same way it does for stored game analysis, WITHOUT
 * needing a gameId/DB row (there isn't one; this position may not even
 * be from a real game). Without this, a FEN-only board had no
 * navigator at all — PLY_RANGE/steppable mode was documented as
 * available for Mode 2, but InlineBoard's ◀▶ controls only ever read
 * from DB-backed game moves, so promising step controls on an
 * evaluate_line board silently produced no controls whatsoever, a real
 * bug (see ChatPage.tsx's runEvaluateLine, which populates this).
 */
class EvaluatedLineStore {
  private readonly lines = new Map<string, EvaluatedLine>();
  private counter = 0;

  register(line: EvaluatedLine): string {
    const id = `el${++this.counter}`;
    this.lines.set(id, line);
    return id;
  }

  get(id: string): EvaluatedLine | undefined {
    return this.lines.get(id);
  }
}

export const evaluatedLineStore = new EvaluatedLineStore();

export interface EvaluatedLineStep {
  san: string;
  from: string;
  to: string;
  /** Position AFTER this step's move. */
  fenAfter: string;
}

export interface EvaluatedLine {
  fen: string;
  bestMoveSan: string;
  /** Full principal variation as SAN moves, starting with bestMoveSan. */
  continuationSan: string[];
  /** Same continuation with per-step from/to/fenAfter, for a client-side steppable board — see evaluated-lines.store.ts. */
  steps: EvaluatedLineStep[];
  /** Centipawns from the mover's perspective at `fen`. */
  evalCp: number;
  mateIn: number | null;
  depth: number;
}

/**
 * Runs a real, on-demand Stockfish search for a specific question the
 * player or Nikola asked about (e.g. "what happens after fxg6") — used
 * by evaluate_line's pendingAction, NOT the passive eval-bar refresh
 * above. Uses the "balanced" preset (deeper search, real MultiPV
 * capability) rather than "quick", since this is a deliberate question
 * that deserves a real answer, not just an instant visual bar. Returns
 * the full continuation as SAN so Nikola can quote a real line instead
 * of only a bare "best move".
 */
export async function evaluateLine(
  fen: string,
  onProgress?: (progress: StockfishSearchProgress) => void,
  signal?: AbortSignal,
): Promise<EvaluatedLine> {
  const { PRESETS } = await import("@/features/analysis/analysis-presets");
  const { applyLegalMove } = await import("@chessla/shared");

  const preset = PRESETS[1];
  // The shared single-threaded interactive engine: a second multi-threaded engine beside background game
  // analysis starves both and can exhaust the tab's memory.
  const result = await liveEval.analyze(fen, preset.depth, signal, onProgress);
  if (!result) throw new Error("Verified line evaluation is unavailable");

  const continuationSan: string[] = [];
  const steps: EvaluatedLineStep[] = [];
  let current = fen;
  const uciMoves = result.bestMove ? [result.bestMove, ...result.pv.slice(1)] : result.pv;
  for (const uci of uciMoves) {
    const move = applyLegalMove(current, uci);
    if (!move) break;
    continuationSan.push(move.san);
    steps.push({ san: move.san, from: move.uci.slice(0, 2), to: move.uci.slice(2, 4), fenAfter: move.fen });
    current = move.fen;
  }

  return {
    fen,
    bestMoveSan: continuationSan[0] ?? "",
    continuationSan,
    steps,
    evalCp: result.cp,
    mateIn: result.mate,
    depth: result.depth,
  };
}
